/**
 * Les plans d'achat (V1.6-08).
 *
 * Un plan répond à une question précise : « voici ce qu'un humain a choisi de
 * figer, réviser puis approuver ». Il n'achète rien. Aucun bouton de cet écran
 * n'envoie quoi que ce soit à un fournisseur, ne réserve, ne reçoit et ne bouge
 * de stock — la commande est un autre objet, et elle n'existe pas encore.
 *
 * **La péremption ne figure pas dans cette liste, et c'est délibéré.** La dire
 * pour chaque ligne demanderait de recalculer autant de comparaisons complètes ;
 * sur cinquante plans, ce serait payer très cher un drapeau que la fiche donne
 * exactement. La liste le dit plutôt que de le taire.
 */
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ClipboardCheck, Filter } from "lucide-react";

import { usePurchasePlans } from "@/api/purchase-plans";
import type { PurchasePlan, PurchasePlanStatus } from "@/api/types";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Select } from "@/components/Input";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { coverageTone, formatCoverage } from "@/lib/comparison-display";
import { formatDate, pluralize } from "@/lib/format";
import {
  formatPlanCode,
  formatPlanStatus,
  formatPlanTotal,
  planStatusTone,
  planTotals,
} from "@/lib/plan-display";

const PAGE_SIZE = 50;

const STATUSES: PurchasePlanStatus[] = ["DRAFT", "APPROVED", "SUPERSEDED", "CANCELLED"];

export function PurchasePlansPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? undefined;
  const offset = Number(params.get("offset") ?? 0);

  const query = useMemo(
    () => ({
      status: status as PurchasePlanStatus | undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [status, offset],
  );

  const { data, isLoading, error, refetch } = usePurchasePlans(query);

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  return (
    <Page>
      <PageHeader
        title="Plans d'achat"
        subtitle="Ce qu'on a décidé d'acheter, figé le jour où on l'a décidé. Rien n'est commandé ici."
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <Select
            value={status ?? ""}
            onChange={(event) => update({ status: event.target.value || undefined })}
            aria-label="Filtrer par statut"
            className="sm:w-56"
          >
            <option value="">Tous les statuts</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {formatPlanStatus(value)}
              </option>
            ))}
          </Select>
          <p className="text-[12px] text-ink-subtle sm:ml-auto">
            La fraîcheur d'un plan se lit sur sa fiche : elle se recalcule, elle ne se stocke pas.
          </p>
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={6} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={
                status ? (
                  <Filter className="size-6" aria-hidden />
                ) : (
                  <ClipboardCheck className="size-6" aria-hidden />
                )
              }
              title={status ? "Aucun plan avec ce statut" : "Aucun plan d'achat"}
              description={
                status
                  ? "Retirez le filtre pour voir les autres."
                  : "Un plan se crée depuis la comparaison d'une demande de prix, une fois les soumissions validées."
              }
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="w-36 whitespace-nowrap">Plan</Th>
                      <Th className="max-w-0">Demande de prix</Th>
                      <Th>Statut</Th>
                      <Th className="hidden @4xl:table-cell">Couverture</Th>
                      <Th align="right" className="hidden whitespace-nowrap @3xl:table-cell">
                        Lignes
                      </Th>
                      <Th align="right" className="whitespace-nowrap">
                        Total
                      </Th>
                      <Th className="hidden whitespace-nowrap @5xl:table-cell">Approuvé</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((plan) => (
                      <PlanRow key={plan.id} plan={plan} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((plan) => (
                  <li key={plan.id}>
                    <PlanCard plan={plan} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>
    </Page>
  );
}

/**
 * Les totaux, une ligne par devise.
 *
 * Jamais de somme toutes devises confondues : additionner des dollars canadiens
 * et américains produirait un nombre que personne ne peut dépenser.
 */
function PlanTotals({ plan }: { plan: PurchasePlan }) {
  const totals = planTotals(plan);
  if (totals.length === 0) return <>—</>;
  return (
    <>
      {totals.map(([currency, amounts]) => (
        <span key={currency} className="block whitespace-nowrap">
          {formatPlanTotal(amounts, currency)}
        </span>
      ))}
    </>
  );
}

function PlanRow({ plan }: { plan: PurchasePlan }) {
  return (
    <Tr>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        <Link to={`/purchase-plans/${plan.id}`} className="hover:text-brand">
          {formatPlanCode(plan)}
        </Link>
      </Td>
      <Td className="max-w-0">
        <Link
          to={`/purchase-plans/${plan.id}`}
          className="block truncate font-medium text-ink hover:text-brand"
        >
          {plan.rfq_code ?? "Demande retirée"}
        </Link>
      </Td>
      <Td>
        <StatusBadge tone={planStatusTone(plan.status)}>{formatPlanStatus(plan.status)}</StatusBadge>
      </Td>
      <Td className="hidden @4xl:table-cell">
        <StatusBadge tone={coverageTone(plan.coverage)}>
          {formatCoverage(plan.coverage)}
        </StatusBadge>
      </Td>
      <Td align="right" className="tnum hidden text-[13px] text-ink-muted @3xl:table-cell">
        {plan.line_count}
      </Td>
      <Td align="right" className="tnum text-[13px] text-ink">
        <PlanTotals plan={plan} />
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-muted @5xl:table-cell">
        {plan.approved_at ? `${plan.approved_by} · ${formatDate(plan.approved_at)}` : "—"}
      </Td>
    </Tr>
  );
}

function PlanCard({ plan }: { plan: PurchasePlan }) {
  return (
    <Link
      to={`/purchase-plans/${plan.id}`}
      className="flex items-start gap-3 px-3 py-3 active:bg-surface-hover"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">
          {plan.rfq_code ?? "Demande retirée"}
        </p>
        <p className="truncate font-mono text-[11px] text-ink-subtle">{formatPlanCode(plan)}</p>
        <p className="tnum mt-0.5 text-[12px] text-ink-muted">
          <PlanTotals plan={plan} />
        </p>
        <p className="mt-0.5 truncate text-[11px] text-ink-subtle">
          {pluralize(plan.line_count, "ligne", "lignes")} ·{" "}
          {pluralize(plan.supplier_count, "fournisseur", "fournisseurs")}
        </p>
      </div>
      <StatusBadge tone={planStatusTone(plan.status)} className="shrink-0">
        {formatPlanStatus(plan.status)}
      </StatusBadge>
    </Link>
  );
}
